import { cn } from '@/modules/shared/lib/utils'
import { useOrderFormStore } from '@/modules/order/stores/order-form.store'
import { useMemo, useEffect, useState } from 'react'
import { useProductStore } from '../stores/product.store'
import { TProducts } from '../types/product.type'

interface ProductCardFooterProps {
	localTotalValue: number | undefined
	valorTotal: number
	product: TProducts
	isCalculatingOrder?: boolean
	hasUnsavedChanges?: boolean
}

export function ProductCardFooterComponent({
	localTotalValue,
	valorTotal,
	product,
	isCalculatingOrder = false,
	hasUnsavedChanges = false
}: ProductCardFooterProps) {
	const { paramsLayoutOrderForm } = useOrderFormStore()
	const { productsInsertedInOrderToCalculation } = useProductStore()

	// Função para truncar valor com precisão específica
	const truncateToPrecision = (value: number, precision: number = 2): number => {
		if (!value || isNaN(value)) return 0;

		const valueStr = value.toString();
		const decimalIndex = valueStr.indexOf('.');

		if (decimalIndex === -1) {
			return value;
		}

		const currentDecimalPlaces = valueStr.length - decimalIndex - 1;

		if (currentDecimalPlaces <= precision) {
			return value;
		}

		const truncatedStr = valueStr.substring(0, decimalIndex + precision + 1);
		const truncated = parseFloat(truncatedStr);
		return truncated;
	};

	// Função para formatar valor com vírgula brasileira
	const formatValue = (value: number): string => {
		if (!value || isNaN(value)) return '0,00';
		return truncateToPrecision(value, 2).toFixed(2).replace('.', ',');
	};

	// Configuração dos campos do footer
	const footerFields = [
		{ field: 'field5', fallback: 'Vr IPI', dataKey: 'vIpi', isValue: true },
		{ field: 'field6', fallback: 'Vr ST', dataKey: 'vIcmsSt', isValue: true },
		{ field: 'field7', fallback: 'Vr Uni', dataKey: 'valorUnTributos', isValue: true },
		{ field: 'field8', fallback: 'Total', dataKey: null, isValue: true },
	];

	// Obter label do campo baseado na configuração ou fallback
	const getFieldLabel = (field: string): string => {
		const fieldConfig = paramsLayoutOrderForm?.geral?.find(
			(item) => item.campo === field
		);
		return fieldConfig?.label ?? footerFields.find(f => f.field === field)?.fallback ?? '';
	};

	// Verificar se o total é zero
	const isTotalZero = (): boolean => {
		const totalValue = localTotalValue !== undefined ? localTotalValue : valorTotal;
		return !totalValue || totalValue === 0;
	};

	// Obter valor do campo baseado na configuração
	const getFieldValue = (field: string, dataKey: string | null, isValue: boolean = false): string => {
		// Se total é zero, retorna 0,00 para todos os campos
		if (isTotalZero()) {
			return '0,00';
		}

		const fieldConfig = paramsLayoutOrderForm?.geral?.find(
			(item) => item.campo === field
		);

		// Tratamento especial para o campo Total (field8)
		if (field === 'field8') {
			const totalValue = valorTotal;

			// Se tem configuração customizada para o campo
			if (fieldConfig?.fieldName) {
				const customValue = product[fieldConfig.fieldName];
				const valueToUse = customValue !== undefined ? customValue : totalValue;

				if (!valueToUse || valueToUse === 0) {
					return '0,00';
				}

				return formatValue(valueToUse);
			}

			// Valor padrão do total
			return totalValue && totalValue !== 0 ? formatValue(totalValue) : '0,00';
		}

		// Para outros campos (field5, field6, field7)
		let rawValue = 0;

		// Primeiro tenta pegar valor do campo customizado
		if (fieldConfig?.fieldName && product[fieldConfig.fieldName] !== undefined) {
			rawValue = product[fieldConfig.fieldName];
		}
		// Senão, usa o dataKey padrão
		else if (dataKey && product[dataKey] !== undefined) {
			rawValue = product[dataKey];
		}

		// Se não há valor, retorna 0,00
		if (!rawValue && rawValue !== 0) {
			return '0,00';
		}

		// Formatação baseada no tipo do campo
		if (fieldConfig?.tipoCampo === 'decimal' || isValue) {
			return formatValue(rawValue);
		}

		// Para campos não decimais, retorna como string sem formatação especial
		return rawValue.toString();
	};

	// Memoização para otimizar performance
	const footerData = useMemo(() => {
		return footerFields.map(({ field, dataKey, isValue }) => ({
			field,
			label: getFieldLabel(field),
			value: getFieldValue(field, dataKey, isValue)
		}));
	}, [paramsLayoutOrderForm, product, localTotalValue, valorTotal]);

	// Estado para controlar animações
	const [isUpdating, setIsUpdating] = useState(false);

	// Efeito para detectar mudanças nos valores
	useEffect(() => {
		if (hasUnsavedChanges) {
			setIsUpdating(true);
			const timer = setTimeout(() => setIsUpdating(false), 300);
			return () => clearTimeout(timer);
		}
	}, [hasUnsavedChanges, localTotalValue, valorTotal]);

	return (
		<div className="mt-1.5 sm:mt-2 p-1.5 sm:p-2 bg-gray-50 rounded border">
			<div className="grid grid-cols-4 gap-1 sm:gap-2 text-center">
				{footerData.map(({ field, label, value }) => (
					<div key={field} className="flex flex-col">
						<span className="text-gray-600 text-[10px] sm:text-[9px] font-medium block mb-0.5">
							{label}
						</span>
						<span className={cn(
							"font-semibold text-[10px] sm:text-xs transition-all duration-200",
							// Estilo especial para o total
							field === 'field8' ? "text-green-600 font-bold text-xs" : "text-gray-800",
							// Efeito de linha cortada para produtos em cálculo
							productsInsertedInOrderToCalculation.includes(product.fkProduto) && "line-through text-gray-400",
							// Opacidade reduzida durante cálculo
							isCalculatingOrder && "opacity-50",
							// Efeito de atualização
							isUpdating && "text-blue-600 scale-105",
							// Destaque para mudanças não salvas
							hasUnsavedChanges && field === 'field8' && "ring-1 ring-orange-300 bg-orange-50 rounded px-1"
						)}>
							{value}
						</span>
					</div>
				))}
			</div>

			{/* Indicador de estado */}
			{(isCalculatingOrder || hasUnsavedChanges) && (
				<div className="mt-1 flex justify-center">
					{isCalculatingOrder && (
						<span className="text-[8px] text-blue-600 animate-pulse">
							Calculando...
						</span>
					)}
					{hasUnsavedChanges && !isCalculatingOrder && (
						<span className="text-[8px] text-orange-600">
							Alterações não salvas
						</span>
					)}
				</div>
			)}
		</div>
	);
}